import type { SearchEngine } from '~/utils/searchSurfaces'
import {
  useClientSearchEngineState,
  useSearchEngineState,
} from '~/composables/search/useSearchEngine'
import {
  SEARCH_ENGINE_COOKIE,
  SEARCH_ENGINE_FORCE_TTL_SECONDS,
  isSearchEngine,
  resolveClientSearchEngine,
  resolveDefaultSearchEngine,
} from '~/utils/searchEngineForce'

declare global {
  interface Window {
    kftSearchEngine?: { force: (engine: SearchEngine) => void }
  }
}

/**
 * Client-side search engine force for A/B tests (KFT-4115).
 *
 * Runs before the app mounts — so before the async A/B tool can act — and
 * reads the force cookie (written by the <head> stub or a previous visit) plus
 * the `?searchEngine=` QA override. The resulting engine is applied right after
 * hydration, never during it: SSR always renders `SEARCH_ENGINE`, and applying
 * a different engine mid-hydration would mismatch SSR-rendered engine branches
 * (navbar/banner autocomplete, listing payload).
 */
export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const engine = useSearchEngineState()
  const clientEngine = useClientSearchEngineState()
  const cookie = useCookie<string | null>(SEARCH_ENGINE_COOKIE, {
    path: '/',
    sameSite: 'lax',
    maxAge: SEARCH_ENGINE_FORCE_TTL_SECONDS,
    // Raw value ('algolia'/'typesense'), matching what the <head> stub writes.
    encode: (value) => value ?? '',
    decode: (value) => value ?? null,
  })

  // Read straight from the URL: the router may not have resolved the initial
  // route yet when plugins run.
  const queryOverride = new URLSearchParams(window.location.search).get(
    'searchEngine'
  )

  const target = resolveClientSearchEngine({
    defaultEngine: resolveDefaultSearchEngine(config.public.searchEngine),
    forced: cookie.value,
    queryOverride,
  })

  // Client-only fetches may use the target straight away (see useSearchEngine).
  clientEngine.value = target

  const applyTarget = () => {
    if (engine.value !== target) engine.value = target
  }

  if (nuxtApp.isHydrating) {
    nuxtApp.hooks.hookOnce('app:suspense:resolve', applyTarget)
  } else {
    applyTarget()
  }

  window.kftSearchEngine = {
    force: (value: SearchEngine) => {
      if (!isSearchEngine(value)) return
      cookie.value = value
      clientEngine.value = value
      engine.value = value
    },
  }
})
